import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ScanLine, Camera, Upload, Loader2, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FinancialTools } from "@/components/FinancialTools";

export default function AnalyzePage() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [credits, setCredits] = useState(null);

  const loadCredits = () => {
    api.get("/credits").then(({ data }) => setCredits(data)).catch(() => {});
  };

  useEffect(() => { loadCredits(); }, []);

  const pick = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size is 10 MB"); return; }
    setFile(f);
    setResult(null);
  };

  const analyze = async () => {
    if (!file) { toast.error("Choose a file or take a photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (question.trim()) fd.append("question", question.trim());
      const { data } = await api.post("/analyze/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      toast.success("Analysis saved to your Documents (+2 points)");
      loadCredits();
      (data.new_achievements || []).forEach((a) =>
        toast.success(`🏆 Achievement unlocked: ${a.label}`, { description: a.desc })
      );
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data?.detail || "Out of free scans", {
          action: { label: "Buy credits", onClick: () => nav("/credits") },
        });
        nav("/credits");
      } else {
        toast.error(err?.response?.data?.detail || "Analysis failed — please try again");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 md:px-10" data-testid="analyze-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI Scanner</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
        Snap it. Upload it. <span className="text-accent">Understand it.</span>
      </h1>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground">
        Photograph or upload any document — your CV, a receipt, handwritten notes, a contract —
        and the AI analyses it instantly. CVs get reviewed, receipts get itemised, ideas become plans.
      </p>

      {credits && !credits.unlimited && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs" data-testid="scan-credit-meter">
          <span className="rounded-full border border-border bg-card px-3 py-1 text-muted-foreground">
            Free scans: <strong className="text-foreground">{credits.free_used} / {credits.free_limit}</strong>
          </span>
          <span className="rounded-full border border-border bg-card px-3 py-1 text-muted-foreground">
            AI credits: <strong className="text-foreground">{credits.credits}</strong>
          </span>
          {credits.free_used >= credits.free_limit && credits.credits <= 0 && (
            <Link to="/credits" className="rounded-full bg-accent px-3 py-1 font-medium text-accent-foreground" data-testid="scan-buy-credits-link">
              Buy credits →
            </Link>
          )}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-border bg-card p-6">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="analyze-file-input" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
          className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="analyze-camera-input" />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button onClick={() => cameraRef.current?.click()}
            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-background p-6 transition-colors hover:border-accent/60"
            data-testid="analyze-camera-btn">
            <Camera className="h-6 w-6 text-accent" />
            <span className="text-sm font-medium">Take a photo</span>
            <span className="text-xs text-muted-foreground">Use your phone camera</span>
          </button>
          <button onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-background p-6 transition-colors hover:border-primary/60"
            data-testid="analyze-upload-btn">
            <Upload className="h-6 w-6 text-primary" />
            <span className="text-sm font-medium">Upload a file</span>
            <span className="text-xs text-muted-foreground">JPG, PNG, HEIC or PDF · max 10 MB</span>
          </button>
        </div>

        {file && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-sm" data-testid="analyze-selected-file">
            <FileText className="h-4 w-4 shrink-0 text-primary" />
            <span className="truncate">{file.name}</span>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
          </div>
        )}

        <div className="mt-4">
          <Label>Anything specific you want to know? (optional)</Label>
          <Input className="mt-1.5" value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Is this CV good enough for a manager role?" data-testid="analyze-question-input" />
        </div>

        <Button onClick={analyze} disabled={busy || !file} size="lg"
          className="mt-5 w-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="analyze-submit-btn">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is reading your document… (~30s)</> : <><ScanLine className="mr-2 h-4 w-4" /> Analyse with AI</>}
        </Button>
      </div>

      {result && (
        <div className="mt-6 rounded-2xl border border-accent/30 bg-card p-6" data-testid="analyze-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <h2 className="font-display text-lg">{result.title}</h2>
          </div>
          <pre className="mt-4 max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-background p-4 font-mono text-xs leading-relaxed">{result.analysis}</pre>
          <div className="mt-4 flex gap-2">
            <Button asChild variant="outline" size="sm" data-testid="analyze-view-docs"><Link to="/documents">View in Documents →</Link></Button>
            <Button size="sm" variant="ghost" onClick={() => { setFile(null); setResult(null); setQuestion(""); }}>Scan another</Button>
          </div>
        </div>
      )}

      <FinancialTools propertyId="default" />
    </div>
  );
}
